import React, { useState } from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@jem-open/jem-ui";
import { Eye, Pencil, Download, Trash2 } from "lucide-react";
import McmAnnouncement from "../services/model/McmAnnouncement";
import { McmAnnouncementStatsApiService } from "../services/api/McmAnnouncementQueryApiService";
import { getDisplayStatus } from "../services/model/McmAnnouncementUtils";

type MessageActionButtonsProps = {
  message: McmAnnouncement;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

const iconBtnClass = "inline-flex items-center justify-center rounded-md h-8 w-8 text-greyscale-text-caption hover:text-greyscale-text-title hover:bg-greyscale-bg-subtle transition-colors";
const iconBtnDisabledClass = "inline-flex items-center justify-center rounded-md h-8 w-8 text-greyscale-border cursor-not-allowed";
const iconBtnDangerClass = "inline-flex items-center justify-center rounded-md h-8 w-8 text-greyscale-text-caption hover:text-red-500 hover:bg-greyscale-bg-subtle transition-colors";

const escapeCSVField = (field: string): string => {
  const sanitized = /^[=+\-@]/.test(field) ? `'${field}` : field;
  if (sanitized.includes(",") || sanitized.includes("\n") || sanitized.includes('"')) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
};

const MessageActionButtons: React.FC<MessageActionButtonsProps> = ({ message, onView, onEdit, onDelete }) => {
  const [isExporting, setIsExporting] = useState(false);
  const canExport = message.status !== "draft";

  const handleExportCsv = async (): Promise<void> => {
    setIsExporting(true);
    try {
      const stats = await McmAnnouncementStatsApiService(message.id);
      const rows: string[] = [];

      // Summary row
      rows.push(["Title", "Status", "Companies", "Total Audience", "Total Read", "Date Published", "Date Closed"].join(","));
      const displayStatus = getDisplayStatus(message);
      rows.push([
        escapeCSVField(message.title),
        escapeCSVField(displayStatus),
        String(message.target_employer_count),
        String(message.audience_snapshot_total),
        String(stats.announcements_read_count),
        message.date_published ? escapeCSVField(new Date(message.date_published).toLocaleString()) : "",
        message.date_closed ? escapeCSVField(new Date(message.date_closed).toLocaleString()) : "",
      ].join(","));

      // Per-company breakdown
      if (stats.per_company && stats.per_company.length > 0) {
        rows.push("");
        rows.push(["Company", "Audience", "Sent", "Read"].join(","));
        stats.per_company.forEach((company) => {
          rows.push([
            escapeCSVField(company.company_name),
            String(company.audience_size),
            String(company.sent_count),
            String(company.read_count),
          ].join(","));
        });
      }

      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mcm-announcement-${message.id}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // Error handled by HttpClientError in service layer
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-xxxs justify-start">
        <Tooltip>
          <TooltipTrigger asChild>
            <button className={iconBtnClass} onClick={onView} aria-label="View message">
              <Eye size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent variant="dark">View</TooltipContent>
        </Tooltip>
        {message.status === "draft" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button className={iconBtnClass} onClick={onEdit} aria-label="Edit message">
                <Pencil size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Edit</TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={canExport && !isExporting ? iconBtnClass : iconBtnDisabledClass}
              onClick={canExport && !isExporting ? handleExportCsv : undefined}
              disabled={!canExport || isExporting}
              aria-label="Export CSV"
            >
              <Download size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent variant="dark">{isExporting ? "Exporting..." : "Export CSV"}</TooltipContent>
        </Tooltip>
        {message.status === "draft" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button className={iconBtnDangerClass} onClick={onDelete} aria-label="Delete message">
                <Trash2 size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Delete</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MessageActionButtons;
